import * as React from "react";
import { useCallback, useContext } from "react";
import {
  Checkbox,
  FormControlLabel,
  Link,
  styled,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { SearchQueriesContext } from "../context/searchQuery";
import { drawerZIndex } from "../components/ddSideBar/constants";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import {
  TERMS_OF_USE_URL,
  COOKIE_POLICY_URL,
  PRIVACY_POLICY_URL,
} from "@kahunmedical/user-management";

//region [[ Styles ]]
const TermsOfServiceView = styled(Dialog)({
  zIndex: drawerZIndex + 2,
});

const AgreeButton = styled(Button)({});

const TermsOfServiceContent = styled(DialogContent)({});

const TermOfServiceActions = styled(DialogActions)(
  (props: TermsOfServiceProps) => ({
    justifyContent: props.showButton ? "flex-end" : "flex-start",
    margin: "30px",
    marginTop: 0,
  }),
);

const TermsCheckBox = styled(Checkbox)({
  "& input": {
    display: "none",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface TermsOfServiceProps {
  showButton?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const TermsOfServiceModal = ({ ...props }: TermsOfServiceProps) => {
  return (
    <TermsOfServiceView
      fullScreen={false}
      open
      aria-labelledby="responsive-dialog-title"
      maxWidth="md"
    >
      <TermsOfService showButton />
    </TermsOfServiceView>
  );
};

export const TermsOfService = ({
  showButton,
  ...props
}: TermsOfServiceProps) => {
  const { setAgreeOnTerms, agreeOnTerms } = useContext(SearchQueriesContext);

  const handleClose = () => {
    evt({
      category: CATEGORY.TERMS_OF_SERVICE,
      action: ACTION.AGREE_TERMS,
    });
    setAgreeOnTerms(true);
  };

  const toggleAgree = useCallback(
    (e) => {
      const { checked } = e?.target || {};
      evt({
        category: CATEGORY.TERMS_OF_SERVICE,
        action: checked ? ACTION.AGREE_TERMS : ACTION.DISAGREE_TERMS,
      });
      setAgreeOnTerms(checked);
    },
    [setAgreeOnTerms],
  );

  const agreedOnTerms = agreeOnTerms; // TODO: user logged in?
  return (
    <>
      <DialogTitle id="responsive-dialog-title">Terms of Service</DialogTitle>
      <TermsOfServiceContent>
        <Typography paragraph>
          BY ACCESSING AND/OR USING KAHUN APPLICATION AND SERVICES
          (COLLECTIVELY, <b>“KAHUN SERVICES”</b>) YOU EXPRESSLY ACKNOWLEDGE AND
          AGREE TO THE FOLLOWING:
        </Typography>
        <Typography paragraph>
          1.
          <b>
            YOU HAVE READ AND AGREE TO KAHUN’S{" "}
            <Link target="_blank" href={TERMS_OF_USE_URL}>
              TERMS OF USE
            </Link>{" "}
            {", "}
            <Link target="_blank" href={PRIVACY_POLICY_URL}>
              PRIVACY POLICY
            </Link>{" "}
            AND{" "}
            <Link target="_blank" href={COOKIE_POLICY_URL}>
              COOKIE POLICY
            </Link>
          </b>
        </Typography>
        <Typography paragraph>
          2.
          <b>
            KAHUN MEDICAL LTD IS NOT A PROVIDER OF MEDICAL ADVICE OR DIAGNOSES.
          </b>
        </Typography>
        <Typography paragraph>
          3. KAHUN SERVICES, INCLUDING ANY INFORMATION, DATA OR ANALYSIS
          GENERATED, PROVIDED OR CONCLUDED FROM THE USE THEREOF ARE NOT INTENDED
          AND SHOULD NOT BE DEEMED MEDICAL OR OTHER HEALTH-RELATED PROFESSIONAL
          ADVICE, DIAGNOSES OR TREATMENT, NOR ARE KAHUN SERVICES INTENDED AS A
          SUBSTITUTE FOR SUCH MEDICAL OR PROFESSIONAL ADVICE, DIAGNOSIS OR
          TREATMENT.)
        </Typography>
        <Typography paragraph>
          4. ANY AND ALL INFORMATION PROVIDED VIA THE KAHUN SERVICES ARE FOR
          INFORMATIONAL PURPOSES ONLY. DO NOT USE KAHUN SERVICES AND/OR TAKE ANY
          SUGGESTIONS PROVIDED BY KAHUN SERVICES AS A SUBSTITUTION FOR MEDICAL
          OR OTHER HEALTH-RELATED PROFESSIONAL ATTENTION. FOR ANY MEDICAL OR
          OTHER PROFESSIONAL ADVICE, YOU SHOULD CONSULT A DOCTOR OR OTHER
          PROFESSIONAL HEALTHCARE PROVIDER.
        </Typography>
      </TermsOfServiceContent>
      <TermOfServiceActions>
        {showButton && (
          <AgreeButton onClick={handleClose} color="primary">
            Agree
          </AgreeButton>
        )}
        {!showButton && (
          <FormControlLabel
            control={
              <TermsCheckBox checked={agreedOnTerms} onChange={toggleAgree} />
            }
            label={
              <span>
                {" "}
                I have read and accepted the{" "}
                <Link target="_blank" href={TERMS_OF_USE_URL}>
                  Terms of use
                </Link>
                ,{" "}
                <Link target="_blank" href={PRIVACY_POLICY_URL}>
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link target="_blank" href={COOKIE_POLICY_URL}>
                  Cookie Policy
                </Link>
              </span>
            }
          />
        )}
      </TermOfServiceActions>
    </>
  );
};
